import { useEffect, ReactNode } from 'react'
import { useSession } from '@supabase/auth-helpers-react'
import { identifyUser, setUserProperties } from '../lib/posthog'
import posthog from '../lib/posthog'

interface PostHogProviderProps {
  children: ReactNode
}

export default function PostHogProvider({ children }: PostHogProviderProps) {
  const session = useSession()

  useEffect(() => {
    if (session?.user) {
      // Identify user in PostHog
      identifyUser(session.user.id, {
        email: session.user.email,
        user_metadata: session.user.user_metadata
      })

      // Set user properties
      setUserProperties({
        email: session.user.email,
        full_name: session.user.user_metadata?.full_name,
        avatar_url: session.user.user_metadata?.avatar_url,
        last_sign_in: session.user.last_sign_in_at,
        created_at: session.user.created_at
      })

      // Track login event
      if (typeof window !== 'undefined') {
        posthog.capture('user_logged_in', {
          user_id: session.user.id,
          email: session.user.email,
          provider: session.user.app_metadata?.provider
        })
      }
    } else {
      // User is not logged in, reset PostHog user
      if (typeof window !== 'undefined') {
        posthog.reset()
      }
    }
  }, [session])

  // Track page views
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    const handleRouteChange = () => {
      posthog.capture('$pageview', {
        path: window.location.pathname,
        url: window.location.href,
        title: document.title
      })
    }

    // Track initial page view
    handleRouteChange()

    // Listen for route changes (you might need to adjust this based on your routing setup)
    window.addEventListener('popstate', handleRouteChange)

    return () => {
      window.removeEventListener('popstate', handleRouteChange)
    }
  }, [])

  return <>{children}</>
}
